import React from "react";
import clsx from "clsx";
import Icon, { type IconName } from "./icon";
import Button from "./forms/button";
import { PalletLangContext } from "../lang-provider";
import styles from "./alert.module.css";

export type AlertSeverity = "error" | "warning" | "info" | "success";

interface AlertProps {
  "data-testid"?: string;
  "data-test-role"?: string;
  severity?: AlertSeverity;
  /** Short, single-line summary of the alert. */
  title: string;
  /** Optional supporting help text or details rendered below the title. */
  children?: React.ReactNode;
  /**
   * Optional trailing reference rendered right-aligned in the header, e.g. a
   * machine-readable error code or line number.
   */
  reference?: React.ReactNode;
  /**
   * When supplied, the alert renders a trailing icon-only dismiss control and
   * calls this on press. When absent, nothing is rendered in its place — the
   * disabled-placeholder pattern is `Modal`'s and is deliberately not copied
   * here (SR-301 phase 7, OQ-4).
   */
  onDismiss?: () => void;
  className?: string;
}

const ICONS: Record<AlertSeverity, IconName> = {
  error: "AlertCircle",
  warning: "AlertTriangle",
  info: "Info",
  success: "CheckCircle",
};

/**
 * An inline, severity-aware callout for surfacing errors, warnings, and
 * informational notices within a page (as opposed to AppError, which owns the
 * whole viewport for fatal failures).
 */
function Alert({
  "data-testid": testId = "pallet-alert",
  "data-test-role": testRole = "alert",
  severity = "error",
  title,
  children,
  reference,
  onDismiss,
  className,
}: AlertProps) {
  const langContext = React.useContext(PalletLangContext);
  const dismissLabel =
    langContext?.simpleTranslate("actions.dismiss") ?? "Dismiss";

  return (
    <div
      role="alert"
      data-testid={testId}
      data-test-role={testRole}
      data-severity={severity}
      className={clsx(styles.alert, styles[severity], className)}
    >
      <span className={styles.icon}>
        <Icon name={ICONS[severity]} size={18} />
      </span>
      <div className={styles.body}>
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
          {/* `reference` then dismiss, deliberately: both land in the same
              corner, and the machine-readable reference reads before the
              control that removes it. The trailing group owns the push-right
              so two competing `margin-left: auto`s cannot split the gap. */}
          {reference || onDismiss ? (
            <span className={styles.headerTrailing}>
              {reference ? (
                <span className={styles.reference}>{reference}</span>
              ) : null}
              {onDismiss ? (
                <Button
                  kind="icon-only"
                  icon="X"
                  variant="bare"
                  label={dismissLabel}
                  onPress={onDismiss}
                />
              ) : null}
            </span>
          ) : null}
        </div>
        {children ? <div className={styles.help}>{children}</div> : null}
      </div>
    </div>
  );
}

export default Alert;
